import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { initializeTripParameters } from '../../utils/travel';
import { TripParameters } from '../../types/chat';

interface RecommendedDestinationsProps {
  onDestinationSelect: (params: TripParameters) => void;
  onGenerate: (params: TripParameters) => void;
}

export const RecommendedDestinations: React.FC<RecommendedDestinationsProps> = ({
  onDestinationSelect,
  onGenerate
}) => {
  const [touchStart, setTouchStart] = React.useState<{ x: number; y: number } | null>(null);

  const handleRecommendedDestination = (destination: string, days: number, startOffset: number) => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() + startOffset);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + days - 1);

    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    const params = {
      ...initializeTripParameters(),
      destination,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      numberOfDays: days,
    };

    // Update the form
    onDestinationSelect(params);

    // Immediately generate the plan
    onGenerate(params);

    // Scroll to top to show the progress
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    });
  };

  const handleTouchEnd = (
    e: React.TouchEvent,
    destination: string,
    days: number,
    startOffset: number
  ) => {
    if (!touchStart) return;

    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
    };

    // Calculate distance moved
    const deltaX = Math.abs(touchEnd.x - touchStart.x);
    const deltaY = Math.abs(touchEnd.y - touchStart.y);

    // Only trigger click if movement is minimal (not a scroll)
    if (deltaX < 10 && deltaY < 10) {
      e.preventDefault(); // Prevent the subsequent click event
      handleRecommendedDestination(destination, days, startOffset);
    }

    setTouchStart(null);
  };

  const handleClick = (
    e: React.MouseEvent,
    destination: string,
    days: number,
    startOffset: number
  ) => {
    // Only handle click on non-touch devices
    if (!touchStart) {
      handleRecommendedDestination(destination, days, startOffset);
    }
  };

  // Generate random but stable offsets for each destination (3-10 days from now)
  const generateRandomOffset = (seed: number) => {
    // Simple seeded random to keep offsets stable during component lifetime
    return 3 + (seed % 8);
  };

  const recommendedDestinations = [
    { name: 'Paris, France', emoji: '🗼', days: 4, startOffset: generateRandomOffset(5), description: 'Romance, art, and iconic landmarks' },
    { name: 'New York, USA', emoji: '🗽', days: 4, startOffset: generateRandomOffset(1), description: 'The city that never sleeps' },
    { name: 'Tokyo, Japan', emoji: '🗾', days: 4, startOffset: generateRandomOffset(7), description: 'Modern meets traditional' },
    { name: 'Rome, Italy', emoji: '🏛️', days: 4, startOffset: generateRandomOffset(3), description: 'Ancient history and timeless beauty' },
    { name: 'London, UK', emoji: '🎡', days: 4, startOffset: generateRandomOffset(9), description: 'History, culture, and royalty' },
  ];

  const formatDateRange = (days: number, startOffset: number) => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() + startOffset);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + days - 1);

    const monthName = startDate.toLocaleString('en-US', { month: 'long' });
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();

    // Check if dates span different months
    if (startDate.getMonth() !== endDate.getMonth()) {
      const endMonthName = endDate.toLocaleString('en-US', { month: 'long' });
      return `${monthName} ${startDay} - ${endMonthName} ${endDay}, ${days} days`;
    }

    return `${monthName} ${startDay}-${endDay}, ${days} days`;
  };

  return (
    <Box sx={{
    position: 'relative',
    width: '100vw',
    left: '50%',
    transform: 'translateX(-50%)',
  }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
          gap: 3,
          width: '100%',
          px: { xs: 2, sm: 3, md: 5 },
        }}
      >
        {recommendedDestinations.map((dest) => (
          <Paper
            key={dest.name}
            elevation={0}
            onClick={(e) => handleClick(e, dest.name, dest.days, dest.startOffset)}
            onTouchStart={handleTouchStart}
            onTouchEnd={(e) => handleTouchEnd(e, dest.name, dest.days, dest.startOffset)}
            sx={(theme) => ({
              p: 3,
              borderRadius: 4,
              border: `2px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backgroundColor: theme.palette.background.paper,
              userSelect: 'none',
              WebkitUserSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              // Only apply hover effects on non-touch devices
              '@media (hover: hover) and (pointer: fine)': {
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderColor: theme.palette.primary.main,
                },
              },
              // Active state for touch devices
              '&:active': {
                transform: 'scale(0.98)',
                opacity: 0.9,
              },
            })}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" sx={{ mb: 1.5 }}>
                {dest.emoji}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {dest.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', mb: 2, minHeight: '2.5em' }}>
                {dest.description}
              </Typography>
              <Chip
                label={formatDateRange(dest.days, dest.startOffset)}
                size="medium"
                icon={<AccessTimeIcon />}
                sx={(theme) => ({
                  fontWeight: 600,
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                })}
              />
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};
